import { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import {
  TrendingUp,
  Loader2,
  AlertCircle,
  Zap,
  CheckCircle2,
  AlertTriangle,
  ChevronRight,
  RefreshCw,
  Info,
  BarChart3,
  Link2,
  ExternalLink,
  ArrowUpRight,
  Circle,
} from 'lucide-react';
import { usePerformanceStore } from '@/stores/skillsIntelligence';
import { DEFAULT_ORG } from '@/stores/skillRegistry';
import type { ArchetypePerformance, VersionPerformance, OptimizationProposal, LoopStatus } from '@/services/api';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const CLIENTS = ['skoutorganic', 'fringesport', 'teddy', 'sylvane'];

const ARCHETYPE_LABELS: Record<string, string> = {
  transformation: 'Transformation',
  social_proof: 'Social Proof',
  authority: 'Authority',
  problem_pain: 'Problem / Pain',
  aspiration: 'Aspiration',
  fear_urgency: 'Fear / Urgency',
  novelty: 'Novelty',
  value_comparison: 'Value Comparison',
  education: 'Education',
  controversy: 'Controversy',
  relatability: 'Relatability',
  curiosity: 'Curiosity',
};

const PRIORITY_CFG: Record<string, { classes: string; dot: string; label: string }> = {
  high: { classes: 'border-rose-200 bg-rose-50', dot: 'bg-rose-500', label: 'High' },
  medium: { classes: 'border-amber-200 bg-amber-50', dot: 'bg-amber-500', label: 'Medium' },
  low: { classes: 'border-blue-200 bg-blue-50', dot: 'bg-blue-400', label: 'Low' },
};

function cesColor(ces: number): string {
  if (ces >= 0.85) return 'text-emerald-600';
  if (ces >= 0.72) return 'text-amber-600';
  return 'text-red-500';
}

function cesBarColor(ces: number): string {
  if (ces >= 0.85) return 'bg-emerald-500';
  if (ces >= 0.72) return 'bg-amber-400';
  return 'bg-red-400';
}

function tierBadge(tier: 'top' | 'mid' | 'low') {
  const cfg = {
    top: 'bg-emerald-100 text-emerald-700',
    mid: 'bg-amber-100 text-amber-700',
    low: 'bg-red-50 text-red-500',
  };
  return (
    <span className={clsx('text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded-full', cfg[tier])}>
      {tier === 'top' ? '🔥 Top' : tier === 'mid' ? '⚡ Mid' : '📉 Low'}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Archetype heatmap
// ---------------------------------------------------------------------------

function ArchetypeHeatmap({ archetypes, loading }: { archetypes: ArchetypePerformance[]; loading: boolean }) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 text-slate-400 text-sm">
        <Loader2 size={14} className="animate-spin" />
        Loading archetype data…
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {archetypes.map((a) => (
        <div key={a.archetype} className="flex items-center gap-3">
          <div className="w-36 text-right flex-shrink-0">
            <span className="text-xs font-medium text-slate-700 truncate">
              {ARCHETYPE_LABELS[a.archetype] ?? a.archetype}
            </span>
          </div>
          <div className="flex-1 h-5 bg-slate-100 rounded overflow-hidden">
            <div
              className={clsx('h-full rounded transition-all', cesBarColor(a.avg_ces))}
              style={{ width: `${(a.avg_ces * 100).toFixed(0)}%` }}
            />
          </div>
          <div className="w-28 flex items-center justify-between gap-2 flex-shrink-0">
            <span className={clsx('text-sm font-bold tabular-nums', cesColor(a.avg_ces))}>
              {a.avg_ces.toFixed(2)}
            </span>
            {tierBadge(a.tier)}
          </div>
          <div className="w-20 text-right flex-shrink-0">
            <span className="text-[11px] text-slate-400 tabular-nums">{a.count} runs</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Version chart
// ---------------------------------------------------------------------------

function VersionChart({ versions, loading }: { versions: VersionPerformance[]; loading: boolean }) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 text-slate-400 text-sm">
        <Loader2 size={14} className="animate-spin" />
        Loading version data…
      </div>
    );
  }
  if (!versions.length) {
    return <p className="text-xs text-slate-400 py-4">No version data available.</p>;
  }

  const maxCes = 1.0;

  return (
    <div className="space-y-3">
      {versions.map((v, i) => {
        const isLatest = i === versions.length - 1;
        const prev = versions[i - 1];
        const delta = prev ? v.avg_ces - prev.avg_ces : null;
        return (
          <div key={v.version} className="flex items-center gap-4">
            <div className="w-16 text-right flex-shrink-0">
              <span className={clsx('text-xs font-mono font-semibold', isLatest ? 'text-emerald-600' : 'text-slate-600')}>
                v{v.version}
              </span>
              {isLatest && (
                <p className="text-[9px] text-emerald-500">latest</p>
              )}
            </div>
            <div className="flex-1 flex items-center gap-2">
              <div className="flex-1 h-6 bg-slate-100 rounded-lg overflow-hidden relative">
                <div
                  className={clsx(
                    'h-full rounded-lg transition-all',
                    isLatest ? 'bg-brand-teal' : 'bg-slate-300',
                  )}
                  style={{ width: `${(v.avg_ces / maxCes) * 100}%` }}
                />
                <div className="absolute inset-y-0 right-2 flex items-center">
                  <span className={clsx('text-[10px] font-bold tabular-nums', cesColor(v.avg_ces))}>
                    {v.avg_ces.toFixed(3)}
                  </span>
                </div>
              </div>
              {delta !== null && (
                <span
                  className={clsx(
                    'text-[11px] font-semibold tabular-nums w-12 text-right flex-shrink-0 flex items-center gap-0.5',
                    delta > 0 ? 'text-emerald-600' : delta < 0 ? 'text-red-500' : 'text-slate-400',
                  )}
                >
                  {delta > 0 && <ArrowUpRight size={11} />}
                  {delta > 0 ? '+' : ''}{(delta * 100).toFixed(1)}
                </span>
              )}
            </div>
            <div className="w-14 text-right flex-shrink-0">
              <span className="text-[10px] text-slate-400">{v.count} runs</span>
            </div>
          </div>
        );
      })}

      <div className="flex items-center gap-2 text-[10px] text-slate-400 mt-2">
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded bg-brand-teal" />
          Latest version
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded bg-slate-300" />
          Previous versions
        </span>
        <span className="ml-2">Delta = CES improvement vs previous version</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Optimization proposals
// ---------------------------------------------------------------------------

function ProposalCard({ proposal }: { proposal: OptimizationProposal }) {
  const cfg = PRIORITY_CFG[proposal.priority] ?? PRIORITY_CFG.low;
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  return (
    <div className={clsx('border rounded-xl p-4 space-y-2.5', cfg.classes)}>
      <div className="flex items-start gap-2.5">
        <div className={clsx('w-2 h-2 rounded-full mt-1.5 flex-shrink-0', cfg.dot)} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-[9px] font-mono uppercase tracking-wide text-slate-500">{cfg.label} priority</span>
            <span className="text-[9px] font-mono text-slate-400 bg-white/50 px-1.5 rounded">
              {proposal.type.replace('_', ' ')}
            </span>
          </div>
          <h4 className="text-sm font-semibold text-navy-900 leading-tight">{proposal.title}</h4>
          <p className="text-xs text-slate-600 mt-1 leading-relaxed">{proposal.body}</p>
        </div>
      </div>

      <div className="flex items-center gap-2 justify-end">
        <button
          onClick={() => setDismissed(true)}
          className="text-[11px] text-slate-400 hover:text-slate-600 transition-colors"
        >
          Dismiss
        </button>
        <button className="flex items-center gap-1 text-[11px] font-medium text-white bg-navy-900 hover:bg-navy-800 px-3 py-1.5 rounded-lg transition-colors">
          {proposal.action_label}
          <ChevronRight size={11} />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loop status
// ---------------------------------------------------------------------------

const LOOP_COLORS = {
  full: 'text-emerald-600',
  partial: 'text-amber-600',
  none: 'text-slate-400',
};

const SIGNAL_SOURCES = [
  { id: 'meta', label: 'Meta Ads Manager', connected: true, client: 'skoutorganic' },
  { id: 'email', label: 'Email ESP', connected: false, client: null },
  { id: 'cloudflare', label: 'Cloudflare Analytics', connected: false, client: null },
];

function LoopStatusTable({ statuses, loading }: { statuses: LoopStatus[]; loading: boolean }) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 text-slate-400 text-sm">
        <Loader2 size={14} className="animate-spin" />
        Loading loop status…
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {statuses.map((s) => (
        <div key={s.client_uid} className="flex items-center gap-4 px-4 py-3 bg-white border border-slate-100 rounded-xl">
          <div className="w-28 flex-shrink-0">
            <p className="text-sm font-semibold text-navy-900">{s.client_uid}</p>
            <span className={clsx('text-[10px] capitalize', LOOP_COLORS[s.loop_completeness])}>
              {s.loop_completeness}
            </span>
          </div>
          <div className="flex-1 grid grid-cols-3 gap-3">
            <LoopCell label="Skills" value={s.skills_active} filled={s.skills_active > 0} />
            <LoopCell label="Assets" value={s.assets_generated} filled={s.assets_generated > 0} />
            <LoopCell label="Signals" value={s.performance_signals} filled={s.performance_signals > 0} />
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {s.loop_completeness === 'full' ? (
              <CheckCircle2 size={16} className="text-emerald-500" />
            ) : s.loop_completeness === 'partial' ? (
              <AlertTriangle size={16} className="text-amber-500" />
            ) : (
              <Circle size={16} className="text-slate-300" />
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function LoopCell({ label, value, filled }: { label: string; value: number; filled: boolean }) {
  return (
    <div className={clsx('text-center rounded-lg px-2 py-1.5', filled ? 'bg-brand-teal/5' : 'bg-slate-50')}>
      <p className={clsx('text-base font-bold', filled ? 'text-brand-teal' : 'text-slate-300')}>{value}</p>
      <p className="text-[9px] text-slate-400 uppercase tracking-wide">{label}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

type TabId = 'archetypes' | 'versions' | 'loop';

export function PerformanceLoopPage() {
  const {
    archetypes,
    versions,
    proposals,
    loopStatuses,
    generatedAt,
    loading,
    error,
    selectedClient,
    setSelectedClient,
    fetch: fetchPerf,
  } = usePerformanceStore();
  const [tab, setTab] = useState<TabId>('archetypes');

  useEffect(() => {
    fetchPerf(DEFAULT_ORG);
  }, [fetchPerf]);

  const topArchetype = archetypes[0];
  const latestVersion = versions[versions.length - 1];
  const previousVersion = versions[versions.length - 2];
  const versionLift =
    latestVersion && previousVersion
      ? latestVersion.avg_ces - previousVersion.avg_ces
      : null;
  const loopsFull = loopStatuses.filter((s) => s.loop_completeness === 'full').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy-900 flex items-center gap-2">
            <TrendingUp size={22} className="text-emerald-500" />
            Performance Loop
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Skills that generate your creative work are informed by how that work performs. The system gets smarter over time.
          </p>
        </div>
        <button
          onClick={() => fetchPerf(DEFAULT_ORG, selectedClient ?? undefined)}
          className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-navy-900 transition-colors"
        >
          <RefreshCw size={13} />
          Refresh
        </button>
      </div>

      {/* Loop flow diagram */}
      <div className="bg-navy-950 rounded-2xl px-6 py-5">
        <div className="flex items-center justify-between gap-2">
          {[
            { label: 'Skill generates asset', icon: Zap, color: 'text-brand-teal' },
            { label: 'Asset goes live', icon: ExternalLink, color: 'text-blue-400' },
            { label: 'Performance flows back', icon: BarChart3, color: 'text-amber-400' },
            { label: 'Skill improves', icon: TrendingUp, color: 'text-emerald-400' },
          ].map((step, i, arr) => (
            <div key={i} className="flex items-center gap-3">
              <div className="flex flex-col items-center gap-1">
                <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                  <step.icon size={18} className={step.color} />
                </div>
                <p className="text-[10px] text-slate-400 text-center max-w-20 leading-tight">{step.label}</p>
              </div>
              {i < arr.length - 1 && (
                <ChevronRight size={16} className="text-slate-600 flex-shrink-0 mb-4" />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard
          label="Top archetype"
          value={topArchetype ? ARCHETYPE_LABELS[topArchetype.archetype] ?? topArchetype.archetype : '—'}
          sub={topArchetype ? `${topArchetype.avg_ces.toFixed(2)} avg CES` : ''}
          color="text-emerald-600"
        />
        <StatCard
          label="Version lift"
          value={versionLift !== null ? `+${(versionLift * 100).toFixed(1)}pts` : '—'}
          sub="latest vs previous"
          color="text-brand-teal"
        />
        <StatCard
          label="Proposals"
          value={proposals.length}
          sub="actionable signals"
          color="text-amber-600"
        />
        <StatCard
          label="Full loops"
          value={`${loopsFull}/${loopStatuses.length}`}
          sub="clients with signals"
          color={loopsFull > 0 ? 'text-emerald-600' : 'text-slate-400'}
        />
      </div>

      {error && (
        <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded-xl px-4 py-3">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {/* Client filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-slate-400">Filter by client:</span>
        <button
          onClick={() => { setSelectedClient(null); fetchPerf(DEFAULT_ORG); }}
          className={clsx(
            'text-xs px-3 py-1 rounded-full border transition-colors',
            !selectedClient
              ? 'bg-navy-900 text-white border-navy-700'
              : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300',
          )}
        >
          All clients
        </button>
        {CLIENTS.map((c) => (
          <button
            key={c}
            onClick={() => { setSelectedClient(c); fetchPerf(DEFAULT_ORG, c); }}
            className={clsx(
              'text-xs px-3 py-1 rounded-full border transition-colors',
              selectedClient === c
                ? 'bg-navy-900 text-white border-navy-700'
                : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300',
            )}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Proposals */}
      {proposals.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Zap size={14} className="text-amber-500" />
            <h3 className="text-sm font-semibold text-navy-900">Optimization Proposals</h3>
            <span className="text-[10px] text-slate-400 ml-auto">
              {generatedAt ? `Generated ${new Date(generatedAt).toLocaleTimeString()}` : ''}
            </span>
          </div>
          <div className="space-y-2">
            {proposals.map((p) => (
              <ProposalCard key={p.proposal_id} proposal={p} />
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-slate-100">
        {(
          [
            { id: 'archetypes', label: 'Angle Archetype Heatmap' },
            { id: 'versions', label: 'Skill Version Comparison' },
            { id: 'loop', label: 'Loop Status' },
          ] as { id: TabId; label: string }[]
        ).map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={clsx(
              'px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors',
              tab === t.id
                ? 'border-emerald-500 text-emerald-600'
                : 'border-transparent text-slate-500 hover:text-navy-900',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div>
        {tab === 'archetypes' && (
          <>
            <div className="flex items-center gap-2 text-xs text-slate-500 mb-4 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
              <Info size={13} className="text-emerald-400 flex-shrink-0" />
              Average CES score per angle archetype across all generated creative runs. Higher = better cognitive engagement.
            </div>
            <ArchetypeHeatmap archetypes={archetypes} loading={loading} />
          </>
        )}
        {tab === 'versions' && (
          <>
            <div className="flex items-center gap-2 text-xs text-slate-500 mb-4 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
              <Info size={13} className="text-emerald-400 flex-shrink-0" />
              CES improvement per published skill version. Each version update is traced through to creative output quality.
            </div>
            <VersionChart versions={versions} loading={loading} />
          </>
        )}
        {tab === 'loop' && (
          <>
            <div className="flex items-center gap-2 text-xs text-slate-500 mb-4 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
              <Info size={13} className="text-emerald-400 flex-shrink-0" />
              For a "full" loop: active skill bindings + asset generation runs + at least one external performance signal connected.
            </div>
            <LoopStatusTable statuses={loopStatuses} loading={loading} />

            {/* Signal connections */}
            <div className="mt-6 space-y-2">
              <p className="text-[10px] font-mono uppercase tracking-wide text-slate-400">Performance signal connections</p>
              {SIGNAL_SOURCES.map((sig) => (
                <div
                  key={sig.id}
                  className={clsx(
                    'flex items-center gap-3 px-4 py-3 border rounded-xl',
                    sig.connected ? 'bg-white border-emerald-100' : 'bg-slate-50/50 border-dashed border-slate-200',
                  )}
                >
                  <Link2 size={14} className={sig.connected ? 'text-emerald-500' : 'text-slate-300'} />
                  <div className="flex-1">
                    <p className={clsx('text-sm font-medium', sig.connected ? 'text-navy-900' : 'text-slate-400')}>
                      {sig.label}
                    </p>
                    {sig.connected && sig.client && (
                      <p className="text-[10px] text-emerald-600">Connected for {sig.client}</p>
                    )}
                  </div>
                  {sig.connected ? (
                    <CheckCircle2 size={14} className="text-emerald-500 flex-shrink-0" />
                  ) : (
                    <button className="text-[10px] font-medium text-brand-teal hover:text-brand-teal-dark transition-colors flex items-center gap-1">
                      Add connection <ChevronRight size={10} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: string | number;
  sub: string;
  color: string;
}) {
  return (
    <div className="bg-white border border-slate-100 rounded-xl px-4 py-3">
      <p className="text-[10px] font-mono uppercase tracking-wide text-slate-400">{label}</p>
      <p className={clsx('text-xl font-bold mt-0.5', color)}>{value}</p>
      {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}
